import { actionsKeys, permissionActions } from "@/lib/utils";
import { setCurrentFinanceYear } from "@/redux/slices";
import { RootState } from "@/redux/store";
import { routes } from "@/routes/utils";
import { FileChartColumnIncreasing, Split } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "@/components/Toast";
import { COST } from "../consts";

const useCostAnalytics = () => {
  const navigate = useNavigate();
  const [openTabs, setOpenTabs] = useState<string[]>([COST]);
  const dispatch = useDispatch();
  const accessPermission = useSelector(
    (state: RootState) => state.currentData.accessPermission
  );

  const communityData = useSelector(
    (state: RootState) => state.currentData.communityData
  );

  const currentFinanceYear = useSelector(
    (state: RootState) => state.currentData.currentFinanceYear
  );
  const [openCopyDistributionConfig, setOpenCopyDistributionConfig] = useState<boolean>(false);

  const [costAnalyticsCards, setCostAnalyticsCards] = useState([
    {
      id: "distribution-metrics-maintanance",
      title: "Create",
      desc: "Allocation Metrics Maintenance",
      icon: FileChartColumnIncreasing,
      link: `${routes.distributionMetricsMaintenance}`,
      subtitle: "",
      hideCount: true,
      apiKey: "draft",
      module: actionsKeys?.DISTRIBUTION_CONFIG?.toLowerCase(),
      accessKeys: permissionActions.READ,
    },
    {
      id: "distribution-rule-configuration",
      title: "Create",
      desc: "Allocation Rule Configuration",
      icon: Split,
      link: `${routes.distributionRuleConfiguration}`,
      subtitle: "",
      hideCount: true,
      apiKey: "draft",
      module: actionsKeys?.DISTRIBUTION_CONFIG?.toLowerCase(),
      accessKeys: permissionActions.READ,
    },
    {
      id: routes.copyDistributionConfiguration,
      title: "View",
      desc: "Copy Allocation Configuration",
      icon: FileChartColumnIncreasing,
      onClick: () => setOpenCopyDistributionConfig(true),
      subtitle: "",
      hideCount: true,
      apiKey: "",
      module: actionsKeys.DISTRIBUTION_CONFIG?.toLowerCase(),
      accessKeys: permissionActions.UPDATE,
    },
    {
      id: "traceability-report",
      title: "View",
      desc: "Traceability Report",
      icon: FileChartColumnIncreasing,
      link: `${routes.traceability}`,
      subtitle: "",
      hideCount: true,
      apiKey: "",
      module: actionsKeys?.TRACEABILITY_BALANCE?.toLowerCase(),
      accessKeys: permissionActions.READ,
    },
    {
      id: routes.closeout,
      title: "View",
      desc: "Level Closeout Report",
      icon: FileChartColumnIncreasing,
      link: `${routes.closeout}`,
      subtitle: "",
      hideCount: true,
      apiKey: "",
      module: actionsKeys.CLOSEOUT_BALANCE?.toLowerCase(),
      accessKeys: permissionActions.READ,
    },
    {
      id: routes.costTrialBalance,
      title: "View",
      desc: "Cost Analytics Trial Balance",
      icon: FileChartColumnIncreasing,
      link: `${routes.costTrialBalance}`,
      subtitle: "",
      hideCount: true,
      apiKey: "",
      module: actionsKeys.COST_ANALYTICS_TRIAL_BALANCE?.toLowerCase(),
      accessKeys: permissionActions.READ,
    },
  ]);

  useEffect(() => {
    if (accessPermission) {
      const cards = costAnalyticsCards?.map((card: any) => ({
        ...card, 
        access: !!accessPermission?.[card.module]?.[card.accessKeys],
      }))
      setCostAnalyticsCards(cards);
    }
  }, [accessPermission]);
  
  const breadcrumbData = useMemo(
    () => [
      {
        link: `/${routes.bom}/${routes.finance}`,
        name: `Activity Dashboard`,
      },
      {
        link: "",
        name: <span className="text-primary">{"Cost Analytics"}</span>,
      },
    ],
    []
  );

  const onBackClick = () => {
    navigate(`/${routes.bom}/${routes.finance}`);
  };

  const onAccordionChange = (e: any) => {
    setOpenTabs(e);
  };

  const onExpandCollapse = () => {
    if (openTabs?.length) {
      setOpenTabs([]);
    } else {
      setOpenTabs([COST]);
    }
  };

  const onCreateCardClick = (link: string) => () => {
    if (communityData) {
      navigate(link);
    } else {
      toast.error("Please select community");
    }
  };

  const onCardClick = (link: string) => () => {
    navigate(link);
  };
  
  const onYearChange = (e: any) => {
    dispatch(setCurrentFinanceYear(e));
  };

  return {
    costAnalyticsCards,
    breadcrumbData,
    onBackClick,
    onAccordionChange,
    openTabs,
    onExpandCollapse,
    onCreateCardClick,
    onCardClick,
    communityData,
    onYearChange,
    currentFinanceYear,
    openCopyDistributionConfig,
    setOpenCopyDistributionConfig,
  };
};

export default useCostAnalytics;
